import React, { useContext, useEffect, useState } from 'react';
import { ELSInlineLoader } from '@els/els-ui-common-react';
import {
  FlashcardDto,
  FlashcardSelectionDto
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import { AppContext } from '../app/AppProvider.component';
import { fetchFlashcardLibraryAction, fetchFlashcardsAction } from '../app/app-state-actions.utilities';
import { FlashcardPlayer } from '../flashcard-player/FlashcardPlayer.component';
import { ShuffleBar, ShuffleOrderButtons } from '../shuffle-bar/ShuffleBar.component';
import { useShuffledOrder } from '../shuffle-bar/useShuffledOrder.hook';
import { getChatContextsForRequest } from '../../pages/ai-chat/ai-chat.utilities';

// Supply exactly one flashcard source:
// - botEntryId: the in-chat flashcards of one entry
// - selection: the Flashcards page's filters, sort and segment. It fetches the whole selection and opens in
//   the list's sort order; a button at the top then shuffles it.
export type FlashcardsPanelProps = {
  botEntryId?: number;
  selection?: FlashcardSelectionDto;
  // Shown at the right of the shuffle row, such as the page's close button, so both stay locked together
  headerEnd?: React.ReactNode;
  // Called when the selection cannot be opened. The page toasts and closes the panel: a toast raised by the
  // panel's own toast service would be unmounted along with it.
  onError?: () => void;
};

type FlashcardsPanelState = {
  // As fetched; for a selection, in the list's sort order
  flashcards: FlashcardDto[];
  // Remounts the player for every shuffle and reset, so it starts again at the first card
  playerKey: number;
  isLoading: boolean;
  errorMessage: string;
};

const defaultState: FlashcardsPanelState = {
  flashcards: null,
  playerKey: 0,
  isLoading: false,
  errorMessage: null,
};

export const FlashcardsPanel = (props: FlashcardsPanelProps): JSX.Element => {

  const {
    appState,
    appStateRef,
    setAppState
  } = useContext(AppContext);

  const [state, setState] = useState<FlashcardsPanelState>(defaultState);
  // Only the Flashcards page's selection is ever shuffled; a chat's flashcards are fixed by their entry
  const order = useShuffledOrder(state.flashcards);

  const restartPlayer = () => {
    setState((prevState) => ({ ...prevState, playerKey: prevState.playerKey + 1 }));
  };

  const handleShuffle = () => {
    order.shuffle();
    restartPlayer();
  };

  const handleResetOrder = () => {
    order.reset();
    restartPlayer();
  };

  // isActive is false once the panel has closed or reopened, so a late response neither updates it nor reports an error.
  // An empty selection (its flashcards deleted since the list loaded) cannot be opened either.
  const loadSelection = (isActive: () => boolean) => {
    const fail = () => {
      setState((prevState) => ({ ...prevState, isLoading: false }));
      props.onError?.();
    };

    fetchFlashcardLibraryAction({
      appState,
      appStateRef,
      setAppState,
      userRole: appState.chatUserRole,
      context: getChatContextsForRequest(appState),
      params: { ...props.selection, unpaged: true },
    })
      .then((response) => {
        if (!isActive()) {
          return;
        }
        if (!response.content.length) {
          fail();
          return;
        }
        setState((prevState) => ({
          ...prevState,
          flashcards: response.content,
          isLoading: false,
        }));
      })
      .catch(() => {
        if (isActive()) {
          fail();
        }
      });
  };

  const loadEntry = (isActive: () => boolean) => {
    fetchFlashcardsAction({
      appState,
      appStateRef,
      setAppState,
      botEntryId: props.botEntryId,
    })
      .then((response) => {
        if (!isActive()) {
          return;
        }
        if (!response.flashcards.length) {
          setState((prevState) => ({
            ...prevState,
            flashcards: null,
            isLoading: false,
            errorMessage: 'Unable to generate flashcards. Please try asking for flashcards again in a new message.',
          }));
        } else {
          setState((prevState) => ({
            ...prevState,
            flashcards: response.flashcards,
            isLoading: false,
            errorMessage: null,
          }));
        }
      })
      .catch((error) => {
        if (!isActive()) {
          return;
        }
        const errorMessage = error?.response?.data?.message || 'Failed to load flashcards';
        setState((prevState) => ({
          ...prevState,
          flashcards: null,
          isLoading: false,
          errorMessage,
        }));
      });
  };

  useEffect(() => {
    let isActive = true;
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      flashcards: null,
      errorMessage: null,
    }));

    if (props.selection) {
      loadSelection(() => isActive);
    } else {
      loadEntry(() => isActive);
    }
    return () => {
      isActive = false;
    };
  }, [props.botEntryId, props.selection]);

  const renderContent = (): JSX.Element => {
    if (state.isLoading) {
      return (
        <div className="u-els-padding">
          <ELSInlineLoader />
          <div className="u-els-margin-top">Loading flashcards...</div>
        </div>
      );
    }

    if (state.errorMessage) {
      return (
        <div className="u-els-padding">
          <div className="u-els-color-warn">{state.errorMessage}</div>
        </div>
      );
    }

    if (state.flashcards) {
      return <FlashcardPlayer key={state.playerKey} flashcards={order.items} />;
    }

    return null;
  };

  // The shuffle controls and the close button stay put in every state
  if (props.selection) {
    return (
      <>
        <ShuffleBar headerEnd={props.headerEnd}>
          <ShuffleOrderButtons
            isShuffled={order.isShuffled}
            isDisabled={!state.flashcards?.length}
            onShuffle={handleShuffle}
            onReset={handleResetOrder}
          />
        </ShuffleBar>
        {renderContent()}
      </>
    );
  }

  return renderContent();
};

export default FlashcardsPanel;
